// DTOs de WS-8 P4 — Facturación por Q (módulo Facturación del coordinador).
//
// Réplica del Excel "_MULTIEMPAQUES Qx" (una hoja por área): la OP se factura
// UNA vez, completa, en el Q en que se CONCILIÓ — no partida por fecha de
// producción. Fila = (OP, SKU) con la cantidad VALIDADA dividida en semanal +
// dominical, tarifa por SKU (precios_sku) y dominical = ×1.8 SOLO si el
// coordinador activó el switch de esa OP (cliente solicitó el domingo).
//
// Decisiones (2026-07-02, usuario, contra el Excel real):
//   - fecha_facturacion = conciliado_at::date si hubo cierre manual; si la OP
//     terminó automática (capturada ≥ programada) → última producción validada.
//   - Solo facturan grupos CON programación viva y TERMINADOS. Lo "en proceso"
//     y lo "sin programar" no factura (el cruce lo gestiona antes).
//   - Se factura SOLO producción VALIDADA por supervisor (✓).
//   - Cortes de Q: 1-15 / 16-22 / 23-fin de mes (evidencia "Q3 MAYO 23 AL 31").
//   - v1 SIN stickers de línea (ATLANTAS facturó $7.4M en sticker — fase
//     siguiente, no descarte) ni Emergentes/Standby.
//
// Tiendas (2026-07-02): el reempaque de tiendas también factura — unidades al
// MISMO precio_sku del cliente + stickers/termos COBRADOS adicional (tarifa en
// costos_insumo). Decisiones v1:
//   - El cliente se deriva del SKU (skus.cliente_id es 1:1) — las tiendas no
//     tienen cliente propio (el destino no es el cliente).
//   - Sin conciliación: cada corrida factura en el CORTE de su fecha_operativa
//     (grupo por cliente+planta+orden_produccion+sku+corte).
//   - v1 SIN recargo dominical (no hay switch por orden de tienda) y sobre
//     estado='valido' (las corridas no tienen validación de supervisor aún —
//     deuda anotada en la auditoría de tiendas).
//
// Endpoints:
//   GET   /api/coordinador/facturacion/q?desde&hasta&cliente_id
//   PATCH /api/coordinador/facturacion/q/dominical
import { z } from 'zod';
import { opLoteOcRegex, opLoteOcMensaje } from './registro_produccion_hora.js';
/** Tarifa dominical = semanal × 1.8 (exacto en todas las hojas del Excel). */
export const RECARGO_DOMINICAL = 1.8;
// =============================================================================
// Query
// =============================================================================
export const facturacionQQuerySchema = z.object({
    desde: z.string().date(),
    hasta: z.string().date(),
    cliente_id: z.string().uuid().optional(),
});
// =============================================================================
// Fila — un grupo (cliente, planta, OP, SKU) terminado, en el Q de su cierre
// =============================================================================
export const facturacionQFilaSchema = z.object({
    /** 'YYYY-MM-Q1' | 'YYYY-MM-Q2' | 'YYYY-MM-Q3' — Q de la fecha de facturación. */
    q_key: z.string(),
    /** conciliado_at::date, o la última producción validada si terminó automática. */
    fecha_facturacion: z.string().date(),
    cliente_id: z.string().uuid(),
    cliente_nombre: z.string(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
    area_id: z.string().uuid(),
    area_codigo: z.string(),
    area_nombre: z.string(),
    numero_op: z.string(),
    sku_id: z.string().uuid(),
    sku_codigo_cliente: z.string(),
    sku_descripcion: z.string(),
    /** Primera y última captura validada del grupo (FECHA DE INICIO / fin). */
    fecha_inicio: z.string().date().nullable(),
    fecha_fin: z.string().date().nullable(),
    cantidad_programada: z.number(),
    /** Validada total = semanal + dominical (solo ✓ del supervisor). */
    cantidad_validada: z.number(),
    /** Parte validada producida en DOMINGO. */
    cantidad_validada_domingo: z.number(),
    /** validada/programada ×100. NULL si programada = 0. */
    cumplimiento_pct: z.number().nullable(),
    /** Tarifa por unidad vigente a la fecha de facturación. NULL = sin precio. */
    precio_unitario: z.number().nullable(),
    /** (validada − domingo) × precio. 0 si no hay precio. */
    valor_semanal: z.number(),
    /** domingo × precio × (dominical_aplica ? 1.8 : 1). */
    valor_dominical: z.number(),
    valor_total: z.number(),
    /** Switch del recargo ×1.8 (el cliente solicitó el domingo). */
    dominical_aplica: z.boolean(),
    /** TRUE si el grupo no tiene precio vigente (factura en 0 — cargarlo en C10). */
    sin_precio: z.boolean(),
    /** TRUE si la metió un cierre manual; FALSE = terminada automática. */
    cerrada_manual: z.boolean(),
});
// =============================================================================
// Rollups — por Q y por área dentro del Q (réplica del CUADRO RESUMEN)
// =============================================================================
export const facturacionQResumenAreaSchema = z.object({
    q_key: z.string(),
    area_id: z.string().uuid(),
    area_codigo: z.string(),
    area_nombre: z.string(),
    ops: z.number().int().min(0),
    unidades_validadas: z.number(),
    valor_total: z.number(),
});
export const facturacionQQSchema = z.object({
    q_key: z.string(),
    /** Rango de fechas del Q (para el encabezado "Q3 · 23–31 may"). */
    desde: z.string().date(),
    hasta: z.string().date(),
    ops: z.number().int().min(0),
    unidades_validadas: z.number(),
    valor_semanal: z.number(),
    valor_dominical: z.number(),
    valor_total: z.number(),
});
// =============================================================================
// Tiendas — grupos (cliente, planta, orden, SKU) por corte de fecha_operativa
// =============================================================================
export const facturacionQTiendaFilaSchema = z.object({
    q_key: z.string(),
    cliente_id: z.string().uuid(),
    cliente_nombre: z.string(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
    /** Orden de la corrida (texto libre — SAP del cliente; puede venir vacío en histórico). */
    orden_produccion: z.string(),
    sku_id: z.string().uuid(),
    sku_codigo_cliente: z.string(),
    sku_descripcion: z.string(),
    fecha_inicio: z.string().date().nullable(),
    fecha_fin: z.string().date().nullable(),
    cantidad_unidades: z.number(),
    cantidad_stickers: z.number(),
    cantidad_termos: z.number(),
    /** Tarifa por unidad (precios_sku, mismo precio que producción). NULL = sin precio. */
    precio_unitario: z.number().nullable(),
    tarifa_sticker: z.number().nullable(),
    tarifa_termo: z.number().nullable(),
    /** unidades × precio. 0 si no hay precio. */
    valor_unidades: z.number(),
    /** stickers × tarifa + termos × tarifa — se cobran adicional al cliente. */
    ingreso_insumos: z.number(),
    valor_total: z.number(),
    sin_precio: z.boolean(),
});
export const facturacionQTiendasSchema = z.object({
    filas: z.array(facturacionQTiendaFilaSchema),
    total: z.object({
        grupos: z.number().int().min(0),
        unidades: z.number(),
        stickers: z.number(),
        termos: z.number(),
        valor_unidades: z.number(),
        ingreso_insumos: z.number(),
        valor_total: z.number(),
        filas_sin_precio: z.number().int().min(0),
    }),
});
// =============================================================================
// Response GET
// =============================================================================
export const facturacionQSchema = z.object({
    desde: z.string().date(),
    hasta: z.string().date(),
    filas: z.array(facturacionQFilaSchema),
    qs: z.array(facturacionQQSchema),
    resumen_areas: z.array(facturacionQResumenAreaSchema),
    /** Reempaque de tiendas del período (factura en el corte de su producción). */
    tiendas: facturacionQTiendasSchema,
    totales: z.object({
        ops: z.number().int().min(0),
        unidades_validadas: z.number(),
        valor_semanal: z.number(),
        valor_dominical: z.number(),
        valor_total: z.number(),
        /** Grupos facturados en 0 por falta de precio vigente. */
        filas_sin_precio: z.number().int().min(0),
    }),
    /**
     * DN4: OPs TERMINADAS (cierre manual o capturada ≥ programada) que aún tienen
     * bloques sin validar. NO entran a `filas` ni a `totales` hasta que supervisión
     * valide todo; se reportan acá para que el coordinador sepa qué retiene el corte.
     */
    pendientes_validacion: z
        .object({
        ops: z.number().int().min(0),
        unidades_pendientes: z.number().min(0),
        /** Valor de esas OPs completas a precio vigente (sin dominical); 0 si sin precio. */
        valor_retenido_estimado: z.number().min(0),
        filas: z.array(z.object({
            q_key: z.string(),
            fecha_facturacion: z.string().date(),
            area_nombre: z.string(),
            numero_op: z.string(),
            sku_codigo_cliente: z.string(),
            sku_descripcion: z.string(),
            cantidad_programada: z.number(),
            cantidad_capturada: z.number(),
            cantidad_validada: z.number(),
            cantidad_pendiente: z.number(),
            /** Bloques hora×línea×SKU sin validar (la regla cuenta bloques, no cantidades). */
            bloques_pendientes: z.number().int().min(0),
            valor_retenido_estimado: z.number().nullable(),
            cerrada_manual: z.boolean(),
        })),
    })
        .optional(),
    clientes: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), es_principal: z.boolean() })),
    /**
     * Meta de facturación mensual del tenant en MILLONES de COP (config de A1,
     * Informe Gerencial 2026-09). Null = no configurada. Va acá para que el
     * informe la lea sin una llamada extra.
     */
    meta_facturacion_mensual_millones: z.number().positive().nullable(),
});
// =============================================================================
// PATCH dominical — switch del recargo por grupo OP
// =============================================================================
export const facturacionQDominicalSchema = z.object({
    cliente_id: z.string().uuid(),
    planta_id: z.string().uuid(),
    numero_op: z.string().trim().regex(opLoteOcRegex, opLoteOcMensaje),
    sku_id: z.string().uuid(),
    dominical_aplica: z.boolean(),
});
export const facturacionQDominicalResultSchema = z.object({
    actualizadas: z.number().int().min(0),
});
//# sourceMappingURL=coordinador_facturacion_q.js.map